import Label, { LabelType } from './Label';
import { SafeAreaView, View } from 'react-native';
import CustomButton from './CustomButton';
import React, { useEffect, useRef } from 'react';
import { faWarning } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import useCustomTheme from '../../hooks/theme-hook';
import useStyles from '../../hooks/styles-hook';
import useTranslation from '../../hooks/translation-hook';
import { FallbackComponentProps } from 'react-native-error-boundary';
import { useDispatch } from 'react-redux';
import { errorActions } from '../../store/errorSlice';
import { useAppSelector } from '../../hooks/store-hook';
import { authActions } from '../../models/helpers/platformImportHelper';
import { LinearGradient } from 'expo-linear-gradient';
import { rootNavigate, rootNavigationRef } from '../../navigation/rootNavigation';
import { getApiErrorStatus, isLockedSessionError, isNetworkError } from '../../helpers/apiErrorHelper';
import { LoggedState } from '../../models/AuthModelsCommon';

const CustomErrorFallback = (props: FallbackComponentProps) => {
  const theme = useCustomTheme();
  const styles = useStyles((selector) => selector.restrictedArea);
  const t = useTranslation();
  const dispatch = useDispatch();
  const authStore = useAppSelector((selector) => selector.auth);
  const hasHandledInvalidMissionStateRef = useRef(false);
  const hasHandledLockedSessionRef = useRef(false);

  const isInvalidMissionAssignationStateError = (error: any) => {
    const rawParts = [
      error?.message,
      error?.response,
      error?.responseText,
      error?.response?.data?.title,
      error?.response?.data?.detail,
      error?.response?.data?.message,
      error?.response?.data,
    ];

    const normalizedMessage = rawParts
      .filter((value) => value !== undefined && value !== null)
      .map((value) => (typeof value === 'string' ? value : JSON.stringify(value)))
      .join(' ')
      .toLowerCase();

    return normalizedMessage.includes('the mission assignation is not in a valid state');
  };

  const navigateAfterBoundaryReset = (routeName: string, params?: any) => {
    let attempts = 0;
    const tryNavigate = () => {
      if (rootNavigationRef.isReady()) {
        rootNavigate(routeName, params);
        return;
      }

      if (attempts < 10) {
        attempts += 1;
        setTimeout(tryNavigate, 50);
      }
    };

    tryNavigate();
  };

  const redirectToMyMissionsAfterBoundaryReset = () => {
    if (typeof document !== 'undefined') {
      const searchParams = new URLSearchParams(document.location.search);
      const isWebview = searchParams.get('webview') === 'true';

      if (isWebview && typeof window !== 'undefined' && (window as any).ReactNativeWebView?.postMessage) {
        (window as any).ReactNativeWebView.postMessage('error');
        return;
      }
    }

    navigateAfterBoundaryReset('mymissions');
  };

  const markSkipSurveyFetchOnce = () => {
    let missionAssignationId = (rootNavigationRef.getCurrentRoute()?.params as any)?.missionAssignationId;

    if (!missionAssignationId && typeof document !== 'undefined') {
      const searchParams = new URLSearchParams(document.location.search);
      missionAssignationId = searchParams.get('missionAssignationId') ?? undefined;
    }

    if (missionAssignationId) {
      (globalThis as any).__skipSurveyFetchOnceMissionAssignationId = missionAssignationId;
    }
  };

  useEffect(() => {
    if (getApiErrorStatus(props.error) === 401) {
      dispatch(authActions.getToken());
    }
  }, [dispatch, props.error]);

  const resetBoundry = () => {
    props.resetError();
  };

  useEffect(() => {
    dispatch(errorActions.setResetError(resetBoundry));
    return () => {
      dispatch(errorActions.setResetError(undefined));
      dispatch(errorActions.setHasError(false));
    };
  }, []);

  useEffect(() => {
    if (props.error) {
      if (!hasHandledLockedSessionRef.current && isLockedSessionError(props.error)) {
        hasHandledLockedSessionRef.current = true;
        dispatch(errorActions.setHasError(false));
        if (authStore.logged !== LoggedState.NotLogged) {
          dispatch(authActions.logout());
        }
        props.resetError();
        navigateAfterBoundaryReset('login', { newLogin: 'true' });
        return;
      }

      if (!hasHandledInvalidMissionStateRef.current && isInvalidMissionAssignationStateError(props.error)) {
        hasHandledInvalidMissionStateRef.current = true;
        markSkipSurveyFetchOnce();
        dispatch(errorActions.setHasError(false));
        props.resetError();
        redirectToMyMissionsAfterBoundaryReset();
        return;
      }

      if (isNetworkError(props.error)) {
        props.resetError();
        return;
      }

      dispatch(errorActions.setHasError(true));
    }
  }, [props.error]);

  return (
    <LinearGradient end={{ x: 0.1, y: 0.4 }} style={{ height: '100%', width: '100%' }} colors={['#035164', '#43a49e']}>
      <SafeAreaView style={[{ alignItems: 'center' }, styles.missionNotLogged.containerBase]}>
        <FontAwesomeIcon style={[{ marginTop: 24 }, styles.missionNotLogged.container.icon]} size={48} icon={faWarning} color={theme.primary.toString()} />
        <Label variant={LabelType.titleLargeDark} style={{ textAlign: 'center', flexShrink: 1, color: 'white', paddingHorizontal: 16 }} numberOfLines={5}>
          {getApiErrorStatus(props.error) === 404 ? t('error-404') : getApiErrorStatus(props.error) === 498 ? t('token-expired') : t('unexpected-error-message')}
        </Label>
        {getApiErrorStatus(props.error) !== 498 && (
          <View style={styles.missionNotLogged.container.button}>
            <CustomButton secondaryColor title={t('retry')} onPress={resetBoundry} />
          </View>
        )}
      </SafeAreaView>
    </LinearGradient>
  );
};

export default CustomErrorFallback;
